// Components
import BoxContainer from "components/BoxContainer";
import PageContainer from "components/PageContainer";
import { Grid, Header, Divider, Button, Loader } from "semantic-ui-react";

// styles
import colors from "assets/colors";
import { CSSProperties, useMemo, useRef, useState } from "react";
import { useRecoilValue } from "recoil";
import { StudentInfoState } from "global-states";
import useAnalysis from "./dashboard-hooks";

import {
  ANALYSIS,
  Analysis,
  AnalysisPayload,
  ANALYSIS_COLOR_KEYS,
  USERANALYSIS,
  UserAnalysis,
} from "./types";
import { useEffect } from "react";
import { Chart, registerables } from "chart.js";
import { mapLabels } from "util/label";
import { useLocationParams } from "hooks";
import NoData from "components/NoData";

const HeaderStyle: CSSProperties = {
  color: colors.darkBlue,
  padding: "1em",
  margin: 0,
};

const DividerStyle: CSSProperties = {
  margin: 0,
};

const GraphContainerStyle: CSSProperties = {
  padding: "1em",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
};

const StudentInfoSpanStyle: CSSProperties = {
  fontWeight: 600,
  marginRight: "1em",
};

const ErrorInfoSpanStyle: CSSProperties = {
  color: "red",
  padding: "1em",
};

const ButtonGroupStyle: CSSProperties = {
  width: "100%",
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  marginBottom: "1em",
};

const ChartContainerStyle: CSSProperties = {
  minHeight: 500,
  height: 600,
  position: "relative",
};

type analysisSelection =
  | "all"
  | "cat_N"
  | "cat_M"
  | "cat_S"
  | "cat_A"
  | "cat_D"
  | "user";

const DashboardPage = () => {
  const student = useRecoilValue(StudentInfoState);
  useLocationParams();
  const { data, error, isLoading } = useAnalysis();

  const [currentAnalysis, setCurrentAnalysis] =
    useState<analysisSelection>("all");

  const [analysisFilter, setAnalysisFilter] = useState<
    (ANALYSIS | USERANALYSIS)[]
  >([ANALYSIS.LQ, ANALYSIS.UQ, ANALYSIS.MEDIAN]);

  function onAnalysisButtonClick(selection: analysisSelection) {
    setCurrentAnalysis(selection);
  }

  const dataExists = useMemo(() => {
    return Object.values(
      data?.data.results[currentAnalysis] ||
        data?.data.pred[currentAnalysis] ||
        {}
    ).some(
      (val) =>
        Object.keys(val).length > 0 &&
        Object.values(val).some((val) => !isNaN(val))
    );
  }, [currentAnalysis, data?.data.pred, data?.data.results]);

  return (
    <PageContainer title={"歷年表現"} showDropdown>
      <Grid columns={1}>
        <Grid.Column>
          <BoxContainer>
            <Header content="歷年表現" style={HeaderStyle} />
            <Divider style={DividerStyle} />
            <div style={GraphContainerStyle}>
              <div>
                <span style={StudentInfoSpanStyle}>
                  班別: {student?.class_name}
                </span>
                <span style={StudentInfoSpanStyle}>
                  學生: {student?.student_name}
                </span>
              </div>
            </div>
            <div style={ErrorInfoSpanStyle}>{error}</div>
            <div style={ButtonGroupStyle}>
              <Button.Group>
                <Button
                  style={{
                    backgroundColor:
                      currentAnalysis === "all" ? "gray" : "lightgray",
                  }}
                  content="平均表現"
                  onClick={() => onAnalysisButtonClick("all")}
                />
                <Button
                  style={{
                    backgroundColor:
                      currentAnalysis === "cat_N" ? "gray" : "lightgray",
                  }}
                  content="數"
                  onClick={() => onAnalysisButtonClick("cat_N")}
                />
                <Button
                  style={{
                    backgroundColor:
                      currentAnalysis === "cat_M" ? "gray" : "lightgray",
                  }}
                  content="度量"
                  onClick={() => onAnalysisButtonClick("cat_M")}
                />
                <Button
                  style={{
                    backgroundColor:
                      currentAnalysis === "cat_S" ? "gray" : "lightgray",
                  }}
                  content="圖形與空間"
                  onClick={() => onAnalysisButtonClick("cat_S")}
                />
                <Button
                  style={{
                    backgroundColor:
                      currentAnalysis === "cat_A" ? "gray" : "lightgray",
                  }}
                  content="代數"
                  onClick={() => onAnalysisButtonClick("cat_A")}
                />
                <Button
                  style={{
                    backgroundColor:
                      currentAnalysis === "cat_D" ? "gray" : "lightgray",
                  }}
                  content="數據處理"
                  onClick={() => onAnalysisButtonClick("cat_D")}
                />
                <Button
                  style={{
                    backgroundColor:
                      currentAnalysis === "user" ? "gray" : "lightgray",
                  }}
                  content="各範疇表現"
                  onClick={() => onAnalysisButtonClick("user")}
                />
              </Button.Group>
            </div>
            <div style={ButtonGroupStyle}>
              {currentAnalysis !== "user"
                ? Object.values(ANALYSIS).map((v, i) => (
                    <Button
                      key={`analysis-${i}`}
                      content={v}
                      onClick={() =>
                        setAnalysisFilter((prev) =>
                          prev.includes(v)
                            ? prev.filter((k) => k !== v)
                            : prev.concat(v)
                        )
                      }
                      style={{
                        backgroundColor: analysisFilter.includes(v)
                          ? "lightgray"
                          : ANALYSIS_COLOR_KEYS[v],
                      }}
                    />
                  ))
                : Object.values(USERANALYSIS).map((v, i) => (
                    <Button
                      key={`user-${i}`}
                      content={v}
                      onClick={() => {
                        setAnalysisFilter((prev) =>
                          prev.includes(v)
                            ? prev.filter((k) => k !== v)
                            : prev.concat(v)
                        );
                      }}
                      style={{
                        backgroundColor: analysisFilter.includes(v)
                          ? "lightgray"
                          : ANALYSIS_COLOR_KEYS[v],
                      }}
                    />
                  ))}
            </div>
            <div style={ChartContainerStyle}>
              <Loader active={isLoading} />
              {dataExists ? (
                <PredictionsGraph
                  data={data}
                  display={currentAnalysis}
                  analysisFilter={analysisFilter}
                />
              ) : (
                <NoData isLoading={isLoading} />
              )}
            </div>
          </BoxContainer>
        </Grid.Column>
      </Grid>
    </PageContainer>
  );
};

const PredictionsGraph = ({
  data,
  display,
  analysisFilter,
}: {
  data?: AnalysisPayload;
  display: analysisSelection;
  analysisFilter: (ANALYSIS | USERANALYSIS)[];
}) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    if (chartRef && chartRef.current && data) {
      Chart.register(...registerables);
      const randomKey = Object.keys(data.data.results[display] || {})[0];
      const dateKeys = Object.keys(
        data.data.results[display]?.[
          randomKey as keyof (Analysis | UserAnalysis)
        ] || {}
      ).concat(
        Object.keys(
          data.data.pred[display]?.[
            randomKey as keyof (Analysis | UserAnalysis)
          ] || {}
        )
      );
      const datasets = Object.entries(data.data.results[display] || {})
        .filter((v) => !analysisFilter.includes(v[0] as ANALYSIS))
        .map(([key, value], i) => {
          return {
            label: mapLabels(key as keyof (Analysis | UserAnalysis)),
            data: Object.values(value),
            fill: ANALYSIS_COLOR_KEYS[key as keyof Analysis],
            borderColor: ANALYSIS_COLOR_KEYS[key as keyof Analysis],
            tension: 0.1,
          };
        })
        .concat(
          Object.entries(data.data.pred[display] || {})
            .filter((v) => !analysisFilter.includes(v[0] as ANALYSIS))
            .map(([key, value], i) => {
              const keys = Object.keys(
                data.data.results[display]?.[
                  key as keyof (Analysis | UserAnalysis)
                ] || {}
              );
              const lastPoint =
                data.data.results[display]?.[
                  key as keyof (Analysis | UserAnalysis)
                ][keys[keys.length - 1]] || 0;
              return {
                label: `${mapLabels(
                  key as keyof (Analysis | UserAnalysis)
                )}(預測)`,
                data: [
                  ...new Array(
                    Object.keys(
                      data.data.results[display]?.[
                        key as keyof (Analysis | UserAnalysis)
                      ] || {}
                    ).length
                      ? Object.keys(
                          data.data.results[display]?.[
                            key as keyof (Analysis | UserAnalysis)
                          ] || {}
                        ).length - 1
                      : 0
                  ),
                ]
                  .map((_) => NaN)
                  .concat([lastPoint, ...Object.values(value)]),
                fill: ANALYSIS_COLOR_KEYS[key as keyof Analysis],
                borderColor: ANALYSIS_COLOR_KEYS[key as keyof Analysis],
                tension: 0.1,
                borderDash: [10, 5],
              };
            })
        );

      const chart = new Chart(chartRef.current, {
        type: "line",
        data: {
          labels: dateKeys,
          datasets: datasets,
        },
        options: {
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  if (
                    ctx.dataIndex !== ctx.dataset.data.length - 1 &&
                    ctx.dataset.label?.includes("預測")
                  ) {
                    return "";
                  }
                  return `${ctx.dataset.label}: ${ctx.formattedValue}`;
                },
              },
            },
          },
          scales: {
            y: {
              title: {
                display: true,
                text: "成績 (%)",
              },
            },
          },
          responsive: true,
          maintainAspectRatio: false,
        },
      });
      return () => {
        chart.destroy();
      };
    }
  }, [analysisFilter, data, display]);
  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        padding: "1em",
      }}
    >
      <canvas ref={chartRef} />
    </div>
  );
};

export default DashboardPage;
